// src/pages/PromotionPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

import { API_BASE } from "../api/config";
import * as XLSX from "xlsx";
import "./PromotionPage.css";

const SHOP_GROUPS = {
  HANGTEN: ["DNGHT", "OSHT", "MT", "GMP", "JS", "JCHT", "MDYHT"],
  PRETTYFIT: ["JCPF", "OSPF", "MDYPF", "DNGPF"],
};

const ALL_SHOPS = [...SHOP_GROUPS.HANGTEN, ...SHOP_GROUPS.PRETTYFIT];

const PROMOTION_GIFT_OPTIONS = [
  { key: "UMBRELLA", code: "UMBRELLA", name: "Umbrella" },
  { key: "CAP", code: "CAP", name: "Cap" },
  { key: "BAG", code: "BAG", name: "Bag" },
  { key: "WATER_BOTTLE", code: "WATER_BOTTLE", name: "Water Bottle" },
  { key: "CUSTOM", code: "", name: "Custom Gift Item" },
];


const PROMOTION_TYPES = {
  PF_TO_HT: {
    label: "PrettyFit → Hangten",
    issueShopGroup: "PRETTYFIT",
    redeemShopGroup: "HANGTEN",
  },
  HT_TO_PF: {
    label: "Hangten → PrettyFit",
    issueShopGroup: "HANGTEN",
    redeemShopGroup: "PRETTYFIT",
  },
  HT_TO_HT: {
    label: "Hangten → Hangten",
    issueShopGroup: "HANGTEN",
    redeemShopGroup: "HANGTEN",
  },
  PF_TO_PF: {
    label: "PrettyFit → PrettyFit",
    issueShopGroup: "PRETTYFIT",
    redeemShopGroup: "PRETTYFIT",
  },
  ALL_TO_ALL: {
    label: "All Shops → All Shops",
    issueShopGroup: "ALL",
    redeemShopGroup: "ALL",
  },
  CUSTOM: {
    label: "Custom",
    issueShopGroup: "CUSTOM",
    redeemShopGroup: "CUSTOM",
  },
};

const defaultForm = {
  promoId: "",
  name: "",
  startDate: "",
  endDate: "",
  startTime: "00:00",
  endTime: "23:59",
  rewardType: "GIFT_ITEM", // GIFT_VOUCHER | INSTANT_DISCOUNT | GIFT_ITEM

  promotionType: "PF_TO_HT",

  issueShopGroup: "PRETTYFIT",
  redeemShopGroup: "HANGTEN",

  customIssueShops: "",
  customRedeemShops: "",

  itemMode: "ALL", // ALL | SELECTED

  minQty: "1",
  minAmount: "0",

  giftAmount: "10000",
  giftExpireDate: "",

  // Gift Item rule - GIFT_ITEM_TIMER_RULE_V2
  giftRule: "ANY_N_ITEMS_GET_GIFT_ITEM",
  excludeSelectedPromotionItems: true,
  giftPresetKey: "UMBRELLA",
  giftTriggerQty: "3",
  giftItemCode: "UMBRELLA",
  giftItemName: "Umbrella",
  giftQty: "1",

  // Promotion anti-fraud timer rule
  promotionTimerEnabled: true,
  triggerTimerOn: "FIRST_ITEM_SCAN",
  sessionMinutes: "30",
  showCountdownLastSeconds: "10",
  cashierCanCancel: false,
  lockLastSeconds: true,
  autoFinalizeOnExpiry: true,

  // Instant Discount flexible rule
  discountRule: "EVERY_N_ITEMS_DISCOUNT_M_ITEMS",
  groupQty: "2",
  discountItemQty: "1",
  discountType: "PERCENT", // PERCENT | AMOUNT | FREE | FIXED_BUNDLE_PRICE
  discountValue: "12",
  discountTarget: "CHEAPEST",

  active: true,
};

const DEFAULT_BUNDLE_GROUPS = [
  {
    groupId: "BUNDLE-90000",
    groupQty: "2",
    bundlePrice: "90000",
    productCodesText: "",
  },
  {
    groupId: "BUNDLE-80000",
    groupQty: "2",
    bundlePrice: "80000",
    productCodesText: "",
  },
];
const DEFAULT_GIFT_AMOUNT_TIERS = [
  { minAmount: "", maxAmount: "", gifts: [{ code: "", name: "", qty: "1" }] },
];
function buildPromoDateTime(dateValue, timeValue, fallbackTime) {
  if (!dateValue) return null;

  const time = timeValue || fallbackTime;
  const d = new Date(`${dateValue}T${time}:00`);

  if (Number.isNaN(d.getTime())) return null;

  return d;
}

function formatDuration(ms) {
  if (ms <= 0) return "0m";

  const totalMinutes = Math.ceil(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

function formatPromoDateTime(value) {
  if (!value) return "-";

  return value.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getPromotionTimeStatus(promo, currentTime) {
  const start = buildPromoDateTime(promo.startDate, promo.startTime, "00:00");

  const end = buildPromoDateTime(promo.endDate, promo.endTime, "23:59");

  if (!start || !end) {
    return {
      type: "UNKNOWN",
      label: "Time not set",
      subText: "",
    };
  }

  const now = currentTime || new Date();

  if (now < start) {
    return {
      type: "COMING_SOON",
      label: `Coming Soon — ${formatDuration(start - now)} left`,
      subText: `Starts: ${formatPromoDateTime(start)}`,
    };
  }

  if (now >= start && now <= end) {
    return {
      type: "ACTIVE",
      label: `Promotion Active — ${formatDuration(end - now)} left`,
      subText: `Ends: ${formatPromoDateTime(end)}`,
    };
  }

  return {
    type: "EXPIRED",
    label: "Promotion Expired",
    subText: `Ended: ${formatPromoDateTime(end)}`,
  };
}
export default function PromotionPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("create");
  const [promotions, setPromotions] = useState([]);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [importedCodes, setImportedCodes] = useState([]);
  const [manualCodesText, setManualCodesText] = useState("");
  const [bundleGroups, setBundleGroups] = useState(DEFAULT_BUNDLE_GROUPS);
  const [giftAmountTiers, setGiftAmountTiers] = useState(DEFAULT_GIFT_AMOUNT_TIERS);
  const [form, setForm] = useState(defaultForm);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const loadPromotions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotions`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        console.error("Load promotions failed:", json);
        setPromotions([]);
        return;
      }

      setPromotions(json.promotions || []);
    } catch (err) {
      console.error("Load promotions error:", err);
      setPromotions([]);
    }
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const normalizeCode = (value) =>
    String(value || "")
      .trim()
      .toUpperCase();

  const parseShopText = (value) => {
    return String(value || "")
      .split(/[,\s]+/)
      .map((s) => normalizeCode(s))
      .filter(Boolean);
  };

  const parseManualProductCodes = (value) =>
    Array.from(
      new Set(
        String(value || "")
          .split(/[,\n\r\t/;]+/)
          .map((code) => normalizeCode(code))
          .filter(Boolean),
      ),
    );

  const getBundleGroupCodeList = (group) =>
    parseManualProductCodes(group?.productCodesText || "");

  const updateBundleGroup = (index, field, value) => {
    setBundleGroups((previous) => {
      const next = previous.map((group, groupIndex) =>
        groupIndex === index ? { ...group, [field]: value } : group,
      );
      const codes = Array.from(
        new Set(next.flatMap((group) => getBundleGroupCodeList(group))),
      );
      setImportedCodes(codes);
      return next;
    });
  };

  const bundleGroupCodes = Array.from(
    new Set(bundleGroups.flatMap((group) => getBundleGroupCodeList(group))),
  );

  const uniqueArray = (arr) => Array.from(new Set(arr.filter(Boolean)));

  const getShopsByGroup = (group, customText = "") => {
    const g = normalizeCode(group);

    if (g === "ALL") return ALL_SHOPS;
    if (g === "HANGTEN") return SHOP_GROUPS.HANGTEN;
    if (g === "PRETTYFIT") return SHOP_GROUPS.PRETTYFIT;
    if (g === "CUSTOM") return parseShopText(customText);

    return [];
  };

  const arrayToMap = (arr) => {
    const obj = {};

    arr.forEach((shop) => {
      obj[shop] = true;
    });

    return obj;
  };

  const filteredPromotions = useMemo(() => {
    const q = search.trim().toUpperCase();

    if (!q) return promotions;

    return promotions.filter((p) => {
      const text = [
        p._key,
        p.promoId,
        p.name,
        p.rewardType,
        p.promotionType,
        p.promotionTypeLabel,
        p.issueShopGroup,
        p.redeemShopGroup,
        p.applyShop,
        p.giftScope,
        p.itemMode,
        p.discountRule,
        p.discountType,
        p.discountValue,
        p.active ? "ACTIVE" : "INACTIVE",
      ]
        .join(" ")
        .toUpperCase();

      return text.includes(q);
    });
  }, [promotions, search]);

  const currentIssueShops = uniqueArray(
    getShopsByGroup(form.issueShopGroup, form.customIssueShops),
  );

  const currentRedeemShops = uniqueArray(
    getShopsByGroup(form.redeemShopGroup, form.customRedeemShops),
  );

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRewardTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      rewardType: value,
    }));

    setMessage("");
  };

  const handlePromotionTypeChange = (value) => {
    const selected = PROMOTION_TYPES[value];

    setForm((prev) => ({
      ...prev,
      promotionType: value,
      issueShopGroup: selected.issueShopGroup,
      redeemShopGroup: selected.redeemShopGroup,
      customIssueShops: value === "CUSTOM" ? prev.customIssueShops : "",
      customRedeemShops: value === "CUSTOM" ? prev.customRedeemShops : "",
    }));
  };

  const handleDiscountTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      discountType: value,
      itemMode: value === "FIXED_BUNDLE_PRICE" ? "SELECTED" : prev.itemMode,
      discountValue:
        value === "FREE"
          ? "100"
          : value === "FIXED_BUNDLE_PRICE"
            ? "90000"
            : value === "PERCENT"
              ? "12"
              : "10000",
      }));

    if (value === "FIXED_BUNDLE_PRICE") {
      setImportedCodes(bundleGroupCodes);
    }
  };

  const makePromoId = () => {
    const now = Date.now().toString().slice(-6);
    return `PROMO-${now}`;
  };

  const makeSafeFirebaseKey = (code) => {
    return String(code)
      .replace(/\./g, "_")
      .replace(/#/g, "_")
      .replace(/\$/g, "_")
      .replace(/\//g, "_")
      .replace(/\[/g, "_")
      .replace(/\]/g, "_");
  };

  const buildSelectedItemsObject = (codes) => {
    const obj = {};

    codes.forEach((code) => {
      const safeKey = makeSafeFirebaseKey(code);

      obj[safeKey] = {
        code,
        active: true,
      };
    });

    return obj;
  };

  const buildBundleSelectedItemsObject = (groups) => {
    const obj = {};

    groups.forEach((group) => {
      group.selectedItemCodes.forEach((code) => {
        const safeKey = makeSafeFirebaseKey(code);
        obj[safeKey] = {
          code,
          active: true,
          bundleGroupId: group.groupId,
          bundlePrice: group.bundlePrice,
          groupQty: group.groupQty,
        };
      });
    });

    return obj;
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
      });

      const codes = [];

      rows.forEach((row) => {
        const possibleCode =
          row.productCode ||
          row.ProductCode ||
          row["Product Code"] ||
          row["product code"] ||
          row.code ||
          row.Code ||
          row.CODE;

        const code = normalizeCode(possibleCode);

        if (code) {
          codes.push(code);
        }
      });

      const uniqueCodes = Array.from(new Set(codes));

      if (uniqueCodes.length === 0) {
        setMessage(
          "Excel ထဲမှာ productCode or code column မတွေ့ပါ။ Header ကို productCode လို့ထားပါ။",
        );
        setImportedCodes([]);
        return;
      }

      setImportedCodes(uniqueCodes);
      setManualCodesText(uniqueCodes.join("\n"));
      setMessage(`✅ Imported ${uniqueCodes.length} product codes`);
    } catch (err) {
      console.error(err);
      setMessage("❌ Excel import failed. Please check file format.");
      setImportedCodes([]);
    }
  };

  const clearImportedCodes = () => {
    setImportedCodes([]);
    setManualCodesText("");
    setMessage("");
  };

  const getInstantRuleLabel = (p) => {
    const groupQty = Number(p.groupQty || 2);
    const discountItemQty = Number(p.discountItemQty || 1);
    const discountType = p.discountType || "PERCENT";
    const discountValue = Number(p.discountValue || 0);

    if (discountType === "FIXED_BUNDLE_PRICE") {
      const groups = Array.isArray(p.bundleGroups)
        ? p.bundleGroups
        : Object.values(p.selectedItems || {})
            .filter((item) => item?.bundleGroupId)
            .reduce((list, item) => {
              let group = list.find(
                (candidate) => candidate.groupId === item.bundleGroupId,
              );
              if (!group) {
                group = {
                  groupId: item.bundleGroupId,
                  bundlePrice: item.bundlePrice,
                  selectedItemCodes: [],
                };
                list.push(group);
              }
              if (item.code) group.selectedItemCodes.push(item.code);
              return list;
            }, []);

      if (groups.length > 0) {
        return groups
          .map((group) => {
            const codes = Array.isArray(group.selectedItemCodes)
              ? group.selectedItemCodes.join(" / ")
              : "Selected items";
            return `${codes} → ${Number(
              group.bundlePrice || group.discountValue || 0,
            ).toLocaleString()} Ks`;
          })
          .join(" · ");
      }
      return `Any ${groupQty} selected items for ${discountValue.toLocaleString()} Ks`;
    }

    if (discountType === "FREE") {
      return `Buy ${groupQty}, cheapest ${discountItemQty} free`;
    }

    if (discountType === "PERCENT") {
      return `Buy ${groupQty}, cheapest ${discountItemQty} ${discountValue}% off`;
    }

    if (discountType === "AMOUNT") {
      return `Buy ${groupQty}, cheapest ${discountItemQty} ${discountValue.toLocaleString()} Ks off`;
    }

    return "-";
  };

  const validateForm = ({ issueShops, redeemShops }) => {
    if (!form.name || !form.startDate || !form.endDate) {
      setMessage("Please fill required fields");
      return false;
    }

    if (new Date(form.endDate) < new Date(form.startDate)) {
      setMessage("End date must be after start date");
      return false;
    }

    if (issueShops.length === 0) {
      setMessage("Issue / Apply shops မရှိပါ။ Shop Group ကိုစစ်ပါ");
      return false;
    }

    if (form.rewardType === "GIFT_VOUCHER" && redeemShops.length === 0) {
      setMessage("Redeem shops မရှိပါ။ Redeem Shop Group ကိုစစ်ပါ");
      return false;
    }

    if (form.itemMode === "SELECTED" && importedCodes.length === 0) {
      setMessage("Selected Items promotion အတွက် product code ထည့်ပါ");
      return false;
    }

    if (
      form.rewardType === "GIFT_VOUCHER" &&
      Number(form.giftAmount || 0) <= 0
    ) {
      setMessage("Gift voucher amount must be greater than 0");
      return false;
    }

    if (form.rewardType === "GIFT_VOUCHER" && !form.giftExpireDate) {
      setMessage("Gift voucher expire date ထည့်ပါ");
      return false;
    }
    if (form.rewardType === "GIFT_ITEM") {
      const giftTriggerQty = Number(form.giftTriggerQty || 0);
      const giftQty = Number(form.giftQty || 0);
      const sessionMinutes = Number(form.sessionMinutes || 0);
      const showCountdownLastSeconds = Number(form.showCountdownLastSeconds || 0);

      if (form.giftRule === "ELIGIBLE_AMOUNT_TIERS") {
        const tiers = giftAmountTiers.map((tier) => ({
          min: tier.minAmount === "" ? NaN : Number(tier.minAmount),
          max: tier.maxAmount === "" ? null : Number(tier.maxAmount),
          gifts: tier.gifts || [],
        })).sort((a, b) => a.min - b.min);
        if (!tiers.length || tiers.some((tier) =>
          !Number.isSafeInteger(tier.min) || tier.min < 0 ||
          (tier.max !== null && (!Number.isSafeInteger(tier.max) || tier.max < tier.min)) ||
          !tier.gifts.length || tier.gifts.some((gift) =>
            !String(gift.code || "").trim() || !String(gift.name || "").trim() ||
            !Number.isSafeInteger(Number(gift.qty)) || Number(gift.qty) < 1
          )
        ) || tiers.some((tier, index) => index > 0 &&
          (tiers[index - 1].max === null || tier.min <= tiers[index - 1].max)
        )) {
          setMessage("Amount tier တွေမှာ valid amount နဲ့ gift ထည့်ပါ။ Tier amount မထပ်ရပါ။");
          return false;
        }
      } else if (giftTriggerQty <= 0) {
        setMessage("Gift Trigger Qty must be greater than 0");
        return false;
      }

      if (form.giftRule !== "ELIGIBLE_AMOUNT_TIERS" && !String(form.giftItemCode || "").trim()) {
        setMessage("Gift Item Code is required");
        return false;
      }

      if (form.giftRule !== "ELIGIBLE_AMOUNT_TIERS" && !String(form.giftItemName || "").trim()) {
        setMessage("Gift Item Name is required");
        return false;
      }

      if (form.giftRule !== "ELIGIBLE_AMOUNT_TIERS" && giftQty <= 0) {
        setMessage("Gift Qty must be greater than 0");
        return false;
      }

      if (form.promotionTimerEnabled === true && sessionMinutes <= 0) {
        setMessage("Session Minutes must be between 1 and 30");
        return false;
      }

      if (form.promotionTimerEnabled === true && showCountdownLastSeconds <= 0) {
        setMessage("Countdown Screen Seconds must be greater than 0");
        return false;
      }

      if (
        form.promotionTimerEnabled === true &&
        showCountdownLastSeconds >= sessionMinutes * 60
      ) {
        setMessage("Countdown Screen Seconds must be smaller than total session time");
        return false;
      }
    }

    if (form.rewardType === "INSTANT_DISCOUNT") {
      const groupQty = Number(form.groupQty || 0);
      const discountItemQty =
        form.discountType === "FIXED_BUNDLE_PRICE"
          ? groupQty
          : Number(form.discountItemQty || 0);
      const discountValue = Number(form.discountValue || 0);

      if (form.discountType === "FIXED_BUNDLE_PRICE") {
        const invalidGroup = bundleGroups.some(
          (group) =>
            getBundleGroupCodeList(group).length === 0 ||
            Number(group.bundlePrice || 0) <= 0 ||
            Number(group.groupQty || groupQty) <= 0,
        );

        if (invalidGroup) {
          setMessage("Bundle group တစ်ခုချင်းစီမှာ product code နဲ့ bundle price ထည့်ပါ");
          return false;
        }
      }

      if (groupQty <= 0) {
        setMessage("Group Qty must be greater than 0");
        return false;
      }

      if (
        form.discountType !== "FIXED_BUNDLE_PRICE" &&
        discountItemQty <= 0
      ) {
        setMessage("Discount Item Qty must be greater than 0");
        return false;
      }

      if (
        form.discountType !== "FIXED_BUNDLE_PRICE" &&
        discountItemQty > groupQty
      ) {
        setMessage("Discount Item Qty cannot be greater than Group Qty");
        return false;
      }

      if (form.discountType !== "FREE" && discountValue <= 0) {
        setMessage("Discount Value must be greater than 0");
        return false;
      }

      if (
        form.discountType === "PERCENT" &&
        (discountValue <= 0 || discountValue > 100)
      ) {
        setMessage("Percent discount must be between 1 and 100");
        return false;
      }
    }

    return true;
  };

  const handleCreatePromotion = async () => {
    const promoId = (form.promoId || makePromoId()).trim().toUpperCase();

    const issueShops = uniqueArray(
      getShopsByGroup(form.issueShopGroup, form.customIssueShops),
    );

    const redeemShops =
      form.rewardType === "GIFT_VOUCHER"
        ? uniqueArray(
            getShopsByGroup(form.redeemShopGroup, form.customRedeemShops),
          )
        : [];

    if (!validateForm({ issueShops, redeemShops })) return;

    try {
      const promotionTypeLabel =
        PROMOTION_TYPES[form.promotionType]?.label || form.promotionType;

      const isGiftVoucher = form.rewardType === "GIFT_VOUCHER";
      const isInstantDiscount = form.rewardType === "INSTANT_DISCOUNT";
      const isGiftItem = form.rewardType === "GIFT_ITEM";

      const normalizedBundleGroups =
        isInstantDiscount && form.discountType === "FIXED_BUNDLE_PRICE"
          ? bundleGroups.map((group, index) => ({
              groupId: String(group.groupId || `BUNDLE-${index + 1}`)
                .trim()
                .toUpperCase(),
              groupQty: Number(group.groupQty || form.groupQty || 2),
              bundlePrice: Number(group.bundlePrice || 0),
              selectedItemCodes: getBundleGroupCodeList(group),
              selectedItems: buildSelectedItemsObject(
                getBundleGroupCodeList(group),
              ),
            }))
          : [];

      const selectedItemCodes =
        form.itemMode === "SELECTED"
          ? normalizedBundleGroups.length > 0
            ? bundleGroupCodes
            : importedCodes
          : [];

      const selectedItems =
        form.itemMode === "SELECTED"
          ? normalizedBundleGroups.length > 0
            ? buildBundleSelectedItemsObject(normalizedBundleGroups)
            : buildSelectedItemsObject(selectedItemCodes)
          : {};

      const groupQty = Number(form.groupQty || 2);
      const discountType = form.discountType;
      const discountItemQty =
        discountType === "FIXED_BUNDLE_PRICE"
          ? groupQty
          : Number(form.discountItemQty || 1);
      const discountValue =
        discountType === "FREE" ? 100 : Number(form.discountValue || 0);

      const promotionPayload = {
        promoId,
        name: form.name.trim(),

        startDate: form.startDate,
        endDate: form.endDate,
        startTime: form.startTime || "00:00",
        endTime: form.endTime || "23:59",
        rewardType: form.rewardType,

        promotionType: form.promotionType,
        promotionTypeLabel,

        issueShopGroup: form.issueShopGroup,
        redeemShopGroup: isGiftVoucher ? form.redeemShopGroup : "",

        issueShops,
        redeemShops,

        issueShopsMap: arrayToMap(issueShops),
        allowedRedeemShops: isGiftVoucher ? arrayToMap(redeemShops) : {},

        applyShop:
          form.issueShopGroup === "ALL"
            ? "ALL"
            : issueShops.length === 1
              ? issueShops[0]
              : issueShops.join(","),

        giftScope: isGiftVoucher
          ? form.redeemShopGroup === "ALL"
            ? "ALL"
            : redeemShops.length === 1
              ? redeemShops[0]
              : form.redeemShopGroup
          : "",

        itemMode: form.itemMode,
        selectedItemCodes,
        selectedItems,
        selectedItemCount: selectedItemCodes.length,

        minQty: isInstantDiscount
          ? groupQty
          : isGiftItem
            ? form.giftRule === "ELIGIBLE_AMOUNT_TIERS" ? 0 : Number(form.giftTriggerQty || 3)
            : Number(form.minQty || 1),
        minAmount: isGiftItem && form.giftRule === "ELIGIBLE_AMOUNT_TIERS" ? 0 : Number(form.minAmount || 0),

        giftAmount: isGiftVoucher ? Number(form.giftAmount || 0) : 0,
        giftExpireDate: isGiftVoucher ? form.giftExpireDate : "",

        // GIFT_ITEM_TIMER_RULE_V2
        giftPresetKey: isGiftItem ? form.giftPresetKey || "UMBRELLA" : "",
        giftRule: isGiftItem ? form.giftRule : "",
        giftTriggerQty: isGiftItem && form.giftRule !== "ELIGIBLE_AMOUNT_TIERS" ? Number(form.giftTriggerQty || 3) : 0,
        excludeSelectedPromotionItems: isGiftItem && form.giftRule === "ELIGIBLE_AMOUNT_TIERS" && form.excludeSelectedPromotionItems === true,
        giftAmountTiers: isGiftItem && form.giftRule === "ELIGIBLE_AMOUNT_TIERS"
          ? giftAmountTiers.map((tier) => ({
              minAmount: Number(tier.minAmount),
              maxAmount: tier.maxAmount === "" ? null : Number(tier.maxAmount),
              gifts: tier.gifts.map((gift) => ({
                code: String(gift.code).trim().toUpperCase(),
                name: String(gift.name).trim(),
                qty: Number(gift.qty),
              })),
            }))
          : [],
        giftItemCode: isGiftItem
          ? String(form.giftItemCode || "").trim().toUpperCase()
          : "",
        giftItemName: isGiftItem ? String(form.giftItemName || "").trim() : "",
        giftQty: isGiftItem ? Number(form.giftQty || 1) : 0,

        promotionTimerEnabled: form.promotionTimerEnabled === true,
        triggerTimerOn: form.triggerTimerOn || "FIRST_ITEM_SCAN",
        sessionMinutes: clampCreateSessionMinutesV2(form.sessionMinutes || 30),
        showCountdownLastSeconds: clampCreateCountdownSecondsV2(
          form.showCountdownLastSeconds || 10,
          form.sessionMinutes || 30,
        ),
        cashierCanCancel: form.cashierCanCancel === true,
        lockLastSeconds: form.lockLastSeconds === true,
        autoFinalizeOnExpiry: form.autoFinalizeOnExpiry === true,

        discountRule: isInstantDiscount ? "EVERY_N_ITEMS_DISCOUNT_M_ITEMS" : "",

        groupQty: isInstantDiscount ? groupQty : 0,
        discountItemQty: isInstantDiscount ? discountItemQty : 0,
        discountType: isInstantDiscount ? discountType : "",
        discountValue: isInstantDiscount ? discountValue : 0,
        discountTarget: isInstantDiscount ? form.discountTarget : "",
        bundleGroups: normalizedBundleGroups,

        active: form.active,
        createdAt: new Date().toISOString(),
      };

      const res = await fetch(`${API_BASE}/api/promotions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(promotionPayload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        setMessage(json?.message || "❌ Promotion create failed");
        return;
      }

      setMessage(`✅ Promotion ${promoId} created successfully`);

      await loadPromotions();

      setForm(defaultForm);
      setImportedCodes([]);
      setManualCodesText("");
      setBundleGroups(DEFAULT_BUNDLE_GROUPS);
      setGiftAmountTiers(DEFAULT_GIFT_AMOUNT_TIERS);
      setActiveTab("list");
    } catch (err) {
      console.error(err);
      setMessage("❌ Error creating promotion");
    }
  };
  const handleToggleActive = async (promo) => {
    const id = promo._key || promo.promoId;

    try {
      console.log("PATCH promotion:", {
        id,
        url: `${API_BASE}/api/promotions/${encodeURIComponent(id)}`,
        active: !promo.active,
      });

      const res = await fetch(
        `${API_BASE}/api/promotions/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            active: !promo.active,
          }),
        },
      );

      const json = await res.json().catch(() => ({}));

      console.log("PATCH response:", {
        status: res.status,
        ok: res.ok,
        json,
      });

      if (!res.ok || !json?.success) {
        alert(json?.message || "Promotion update failed");
        return;
      }

      await loadPromotions();
    } catch (err) {
      console.error("Promotion update fetch error:", err);
      alert(`Update failed: ${err.message}`);
    }
  };


  const handleGiftPresetChange = (giftKey) => {
    const selectedGift =
      PROMOTION_GIFT_OPTIONS.find((gift) => gift.key === giftKey) ||
      PROMOTION_GIFT_OPTIONS[0];

    setForm((prev) => ({
      ...prev,
      giftPresetKey: giftKey,
      giftItemCode:
        selectedGift.key === "CUSTOM"
          ? ""
          : String(selectedGift.code || "").toUpperCase(),
      giftItemName:
        selectedGift.key === "CUSTOM" ? "" : String(selectedGift.name || ""),
    }));
  };

  const renderGiftItemTimerFields = () => (
    <>
      {form.rewardType === "GIFT_ITEM" && (
        <div className="promo-section-card promo-section-gift">
          <div className="promo-section-title">
            <div>
              <h4>Gift Item Settings</h4>
              <p>Item quantity သို့မဟုတ် သတ်မှတ်ထားတဲ့ amount အလိုက် gift ပေးနိုင်ပါတယ်။</p>
            </div>
            <span className="promo-section-pill">Gift Promotion</span>
          </div>

          <div className="promo-inner-grid">
            <div className="promo-field promo-wide-field">
              <label>Gift Rule</label>
              <select value={form.giftRule} onChange={(e) => setForm((prev) => ({
                ...prev, giftRule: e.target.value,
                promotionTimerEnabled: e.target.value === "ELIGIBLE_AMOUNT_TIERS" ? false : prev.promotionTimerEnabled,
              }))}>
                <option value="ANY_N_ITEMS_GET_GIFT_ITEM">Item Quantity</option>
                <option value="ELIGIBLE_AMOUNT_TIERS">Amount Tiers</option>
              </select>
            </div>
            {form.giftRule === "ELIGIBLE_AMOUNT_TIERS" ? (
              <div className="promo-field promo-wide-field">
                <label>
                  <input type="checkbox" checked={form.excludeSelectedPromotionItems}
                    onChange={(e) => handleChange("excludeSelectedPromotionItems", e.target.checked)} />
                  {" "}Exclude items in active Selected Item instant promotions
                </label>
                <small>Selected Item code ထဲပါတဲ့ item ရဲ့ amount ကို gift threshold မှာ လုံးဝ မတွက်ပါ။</small>
                {giftAmountTiers.map((tier, tierIndex) => (
                  <div className="promo-bundle-group promo-amount-tier" key={tierIndex}>
                    <div className="promo-bundle-group-heading">
                      <strong>Amount Tier {tierIndex + 1}</strong>
                      <button type="button" onClick={() => setGiftAmountTiers((prev) => prev.filter((_, i) => i !== tierIndex))}>Remove tier</button>
                    </div>
                    <div className="promo-bundle-group-fields">
                      <label>From Ks (inclusive)
                        <input type="number" min="0" step="1" value={tier.minAmount} placeholder="99700"
                          onChange={(e) => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, minAmount: e.target.value } : row))} />
                      </label>
                      <label>To Ks (inclusive, blank = unlimited)
                        <input type="number" min="0" step="1" value={tier.maxAmount} placeholder="199699"
                          onChange={(e) => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, maxAmount: e.target.value } : row))} />
                      </label>
                    </div>
                    {tier.gifts.map((gift, giftIndex) => (
                      <div className="promo-bundle-group-fields promo-amount-gift-fields" key={giftIndex}>
                        <input value={gift.code} placeholder="Gift code (e.g. HANGTEN_BOX)"
                          onChange={(e) => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, gifts: row.gifts.map((g, j) => j === giftIndex ? { ...g, code: e.target.value } : g) } : row))} />
                        <input value={gift.name} placeholder="Gift name"
                          onChange={(e) => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, gifts: row.gifts.map((g, j) => j === giftIndex ? { ...g, name: e.target.value } : g) } : row))} />
                        <input type="number" min="1" step="1" value={gift.qty} placeholder="Qty"
                          onChange={(e) => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, gifts: row.gifts.map((g, j) => j === giftIndex ? { ...g, qty: e.target.value } : g) } : row))} />
                        <button type="button" onClick={() => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, gifts: row.gifts.filter((_, j) => j !== giftIndex) } : row))}>Remove gift</button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setGiftAmountTiers((prev) => prev.map((row, i) => i === tierIndex ? { ...row, gifts: [...row.gifts, { code: "", name: "", qty: "1" }] } : row))}>Add gift</button>
                  </div>
                ))}
                <button type="button" onClick={() => setGiftAmountTiers((prev) => [...prev, { minAmount: "", maxAmount: "", gifts: [{ code: "", name: "", qty: "1" }] }])}>Add amount tier</button>
              </div>
            ) : (<>
            <div className="promo-field">
              <label>Promotion Gift Name</label>
              <select
                value={form.giftPresetKey || "UMBRELLA"}
                onChange={(e) => handleGiftPresetChange(e.target.value)}
              >
                {PROMOTION_GIFT_OPTIONS.map((gift) => (
                  <option key={gift.key} value={gift.key}>
                    {gift.name}
                  </option>
                ))}
              </select>
              <small>Gift item ကို ဒီ box ကနေရွေးပါ</small>
            </div>

            <div className="promo-field">
              <label>Gift Trigger Qty</label>
              <input
                type="number"
                min="1"
                value={form.giftTriggerQty}
                onChange={(e) => handleChange("giftTriggerQty", e.target.value)}
                placeholder="3"
              />
              <small>ဥပမာ 3 ဆို item 3 ခုရောက်တာနဲ့ gift ကျမယ်</small>
            </div>

            <div className="promo-field">
              <label>Gift Qty</label>
              <input
                type="number"
                min="1"
                value={form.giftQty}
                onChange={(e) => handleChange("giftQty", e.target.value)}
                placeholder="1"
              />
            </div>

            <div className="promo-field">
              <label>Gift Item Code</label>
              <input
                value={form.giftItemCode}
                readOnly={(form.giftPresetKey || "UMBRELLA") !== "CUSTOM"}
                onChange={(e) =>
                  handleChange("giftItemCode", e.target.value.toUpperCase())
                }
                placeholder="UMBRELLA"
              />
            </div>

            <div className="promo-field promo-wide-field">
              <label>Gift Item Name</label>
              <input
                value={form.giftItemName}
                readOnly={(form.giftPresetKey || "UMBRELLA") !== "CUSTOM"}
                onChange={(e) => handleChange("giftItemName", e.target.value)}
                placeholder="Umbrella"
              />
            </div>
            </>)}
          </div>
        </div>
      )}

      <div className="promo-section-card promo-section-timer">
        <div className="promo-section-title">
          <div>
            <h4>Timer & Anti-Fraud Settings</h4>
            <p>First item scan ဖြစ်တာနဲ့ timer စမယ်၊ နောက်ဆုံး seconds မှာ lock screen ပေါ်မယ်</p>
          </div>
          <span className="promo-section-pill">Screen Time</span>
        </div>

        <div className="promo-inner-grid">
          <div className="promo-field">
            <label>Promotion Timer</label>
            <select
              value={form.promotionTimerEnabled ? "yes" : "no"}
              onChange={(e) =>
                handleChange("promotionTimerEnabled", e.target.value === "yes")
              }
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </div>

          <div className="promo-field">
            <label>Timer Start</label>
            <select
              value={form.triggerTimerOn}
              onChange={(e) => handleChange("triggerTimerOn", e.target.value)}
            >
              <option value="FIRST_ITEM_SCAN">First Item Scan</option>
            </select>
          </div>

          <div className="promo-field">
            <label>Session Minutes 1–30</label>
            <input
              type="number"
              min="1"
              value={form.sessionMinutes}
              onChange={(e) => handleChange("sessionMinutes", e.target.value)}
              placeholder="30"
            />
            <small>Scan ဖတ်ပြီး ဘယ်နှမိနစ်အတွင်း sale ပြီးရမလဲ</small>
          </div>

          <div className="promo-field">
            <label>Countdown Screen Seconds</label>
            <input
              type="number"
              min="1"
              value={form.showCountdownLastSeconds}
              onChange={(e) =>
                handleChange("showCountdownLastSeconds", e.target.value)
              }
              placeholder="10"
            />
            <small>နောက်ဆုံး ဘယ်နှစက္ကန့်မှာ full screen lock ပြမလဲ</small>
          </div>

          <div className="promo-field">
            <label>Cashier Cancel</label>
            <select
              value={form.cashierCanCancel ? "yes" : "no"}
              onChange={(e) =>
                handleChange("cashierCanCancel", e.target.value === "yes")
              }
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>

          <div className="promo-field">
            <label>Last Seconds Lock</label>
            <select
              value={form.lockLastSeconds ? "yes" : "no"}
              onChange={(e) =>
                handleChange("lockLastSeconds", e.target.value === "yes")
              }
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </div>

          <div className="promo-field">
            <label>Auto Finalize On Expiry</label>
            <select
              value={form.autoFinalizeOnExpiry ? "yes" : "no"}
              onChange={(e) =>
                handleChange("autoFinalizeOnExpiry", e.target.value === "yes")
              }
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </select>
          </div>
        </div>
      </div>
    </>
  );



  // ERP_CREATE_TIME_SCREEN_TIME_V2
  const clampCreateSessionMinutesV2 = (value) => {
    const n = Number(value || 1);
    if (!Number.isFinite(n)) return 1;
    return Math.max(1, Math.min(30, Math.floor(n)));
  };

  const clampCreateCountdownSecondsV2 = (
    value,
    minutesValue = form.sessionMinutes,
  ) => {
    const sessionMinutes = clampCreateSessionMinutesV2(minutesValue);
    const maxSeconds = Math.max(1, sessionMinutes * 60 - 1);
    const n = Number(value || 1);

    if (!Number.isFinite(n)) return 1;

    return Math.max(1, Math.min(maxSeconds, Math.floor(n)));
  };

  const applyCreateScreenTimeV2 = (minutesValue) => {
    const minutes = clampCreateSessionMinutesV2(minutesValue);
    const countdown = Math.min(10, Math.max(1, minutes * 60 - 1));

    setForm((prev) => ({
      ...prev,
      promotionTimerEnabled: true,
      triggerTimerOn: "FIRST_ITEM_SCAN",
      sessionMinutes: String(minutes),
      showCountdownLastSeconds: String(countdown),
      cashierCanCancel: false,
      lockLastSeconds: true,
      autoFinalizeOnExpiry: true,
    }));
  };

  const handleCreateScreenTimeManualV2 = (value) => {
    const minutes = clampCreateSessionMinutesV2(value);
    const countdown = clampCreateCountdownSecondsV2(
      form.showCountdownLastSeconds || 10,
      minutes,
    );

    setForm((prev) => ({
      ...prev,
      promotionTimerEnabled: true,
      triggerTimerOn: "FIRST_ITEM_SCAN",
      sessionMinutes: String(minutes),
      showCountdownLastSeconds: String(countdown),
      cashierCanCancel: false,
      lockLastSeconds: true,
      autoFinalizeOnExpiry: true,
    }));
  };

  const handleCreateCountdownManualV2 = (value) => {
    const seconds = clampCreateCountdownSecondsV2(
      value,
      form.sessionMinutes || 30,
    );

    setForm((prev) => ({
      ...prev,
      showCountdownLastSeconds: String(seconds),
    }));
  };

  return (
    <Layout title="Promotions">


      <div className="promo-page">
        <div className="promo-header">
          <div>
            <h2>Promotion Management</h2>
            <p>
              Create gift voucher or flexible instant discount promotions by
              date, shop group, item mode, and amount.
            </p>
          </div>
        </div>

        <div className="promo-tabs">
          <button
            type="button"
            className={activeTab === "create" ? "active" : ""}
            onClick={() => setActiveTab("create")}
          >
            Create Promotion
          </button>

          <button
            type="button"
            className={activeTab === "list" ? "active" : ""}
            onClick={() => setActiveTab("list")}
          >
            Promotion List
          </button>
        </div>

        {activeTab === "create" ? (
          <div className="promo-card">
            <h3>
              {form.rewardType === "GIFT_VOUCHER"
                ? "Create Gift Voucher Promotion"
                : form.rewardType === "GIFT_ITEM"
                  ? "Create Gift Item Promotion"
                  : "Create Instant Discount Promotion"}
            </h3>

            <div className="promo-grid">
              <div className="promo-field">
                <label>Promotion ID</label>
                <input
                  value={form.promoId}
                  onChange={(e) =>
                    handleChange("promoId", e.target.value.toUpperCase())
                  }
                  placeholder="Auto if empty"
                />
              </div>

              <div className="promo-field">
                <label>Promotion Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Buy 2 Cheapest 1 Item 12% Off"
                />
              </div>

              <div className="promo-field">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                />
              </div>
              <div className="promo-form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="promo-field">
                <label>End Date *</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => handleChange("endDate", e.target.value)}
                />
              </div>
              <div className="promo-form-group">
                <label>End Time</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="promo-field">
                <label>Reward Type</label>
                <select
                  value={form.rewardType}
                  onChange={(e) => handleRewardTypeChange(e.target.value)}
                >
                  <option value="GIFT_VOUCHER">Gift Voucher</option>
                  <option value="INSTANT_DISCOUNT">Instant Discount</option>
                  <option value="GIFT_ITEM">Gift Item / Umbrella</option>
                </select>
                <small>
                  Gift Voucher ဆို coupon code ထွက်မယ်။ Instant Discount ဆို
                  sale total ထဲမှာချက်ချင်းလျှော့မယ်။
                </small>
              </div>

              <div className="promo-field">
                <label>
                  {form.rewardType === "GIFT_VOUCHER"
                    ? "Promotion Flow"
                    : "Apply Shop Flow"}
                </label>
                <select
                  value={form.promotionType}
                  onChange={(e) => handlePromotionTypeChange(e.target.value)}
                >
                  {Object.entries(PROMOTION_TYPES).map(([key, item]) => (
                    <option key={key} value={key}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <small>
                  Instant Discount မှာ အရှေ့က shop group ကို discount apply
                  လုပ်မယ်။ Gift Voucher မှာ အနောက်က group ကို redeem shop
                  အဖြစ်သုံးမယ်။
                </small>
              </div>

              <div className="promo-field">
                <label>Issue / Apply Shop Group</label>
                <select
                  value={form.issueShopGroup}
                  onChange={(e) =>
                    handleChange("issueShopGroup", e.target.value)
                  }
                  disabled={form.promotionType !== "CUSTOM"}
                >
                  <option value="PRETTYFIT">PrettyFit Shops</option>
                  <option value="HANGTEN">Hangten Shops</option>
                  <option value="ALL">All Shops</option>
                  <option value="CUSTOM">Custom Shops</option>
                </select>
                <small>ဒီ group ထဲက ဆိုင်တွေမှာ promotion သက်ရောက်မယ်။</small>
              </div>

              {form.issueShopGroup === "CUSTOM" && (
                <div className="promo-field">
                  <label>Custom Issue / Apply Shops</label>
                  <input
                    value={form.customIssueShops}
                    onChange={(e) =>
                      handleChange("customIssueShops", e.target.value)
                    }
                    placeholder="Ex: DNGHT,OSHT"
                  />
                </div>
              )}

              {form.rewardType === "GIFT_VOUCHER" && (
                <>
                  <div className="promo-field">
                    <label>Redeem Shop Group</label>
                    <select
                      value={form.redeemShopGroup}
                      onChange={(e) =>
                        handleChange("redeemShopGroup", e.target.value)
                      }
                      disabled={form.promotionType !== "CUSTOM"}
                    >
                      <option value="HANGTEN">Hangten Shops</option>
                      <option value="PRETTYFIT">PrettyFit Shops</option>
                      <option value="ALL">All Shops</option>
                      <option value="CUSTOM">Custom Shops</option>
                    </select>
                    <small>
                      ထွက်လာတဲ့ gift voucher ကို ဒီ group ထဲက ဆိုင်တွေမှာပဲ
                      ပြန်သုံးခွင့်ရှိမယ်။
                    </small>
                  </div>

                  {form.redeemShopGroup === "CUSTOM" && (
                    <div className="promo-field">
                      <label>Custom Redeem Shops</label>
                      <input
                        value={form.customRedeemShops}
                        onChange={(e) =>
                          handleChange("customRedeemShops", e.target.value)
                        }
                        placeholder="Ex: DNGHT,OSHT,JCHT"
                      />
                    </div>
                  )}
                </>
              )}

              <div className="promo-field promo-shop-preview">
                <label>Issue / Apply Shops Preview</label>
                <div className="promo-chip-box">
                  {currentIssueShops.map((shop) => (
                    <span key={shop}>{shop}</span>
                  ))}
                </div>
              </div>

              {form.rewardType === "GIFT_VOUCHER" && (
                <div className="promo-field promo-shop-preview">
                  <label>Redeem Shops Preview</label>
                  <div className="promo-chip-box">
                    {currentRedeemShops.map((shop) => (
                      <span key={shop}>{shop}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="promo-field">
                <label>Required Item Mode</label>
                <select
                  value={form.itemMode}
                  onChange={(e) => {
                    handleChange("itemMode", e.target.value);
                    setImportedCodes([]);
                    setManualCodesText("");
                    setMessage("");
                  }}
                >
                  <option value="ALL">All Items</option>
                  <option value="SELECTED">Selected Product Codes</option>
                </select>
                <small>
                  Selected ကိုရွေးရင် product code အလိုက်ပဲစစ်ပြီး size နဲ့ color
                  အားလုံးအတွက် promotion သက်ရောက်မယ်။
                </small>
              </div>

              {form.itemMode === "SELECTED" &&
                form.discountType !== "FIXED_BUNDLE_PRICE" && (
                <div className="promo-field promo-wide-field">
                  <label>Product Codes (Manual Entry)</label>
                  <textarea
                    value={manualCodesText}
                    onChange={(e) => {
                      setManualCodesText(e.target.value);
                      setImportedCodes(parseManualProductCodes(e.target.value));
                    }}
                    placeholder={
                      "10529-031-001-70\n10539-031-001-70"
                    }
                  />
                  <small>
                    Code တစ်ကြောင်းစီထည့်ပါ။ Comma သို့မဟုတ် slash နဲ့လည်း
                    ခွဲထည့်နိုင်ပါတယ်။
                  </small>
                </div>
              )}

              {form.itemMode === "SELECTED" &&
                form.discountType !== "FIXED_BUNDLE_PRICE" && (
                <div className="promo-field promo-import-field">
                  <label>Excel Import (Optional)</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleExcelImport}
                  />
                  <small>
                    Excel header ကို <b>productCode</b> သို့မဟုတ် <b>code</b>{" "}
                    လို့ထားပါ။ Import လုပ်ရင် manual list ကို အစားထိုးမယ်။
                  </small>
                </div>
              )}

              {form.itemMode === "SELECTED" && (
                <div className="promo-field">
                  <label>Selected Count</label>
                  <div className="promo-import-box">
                    <strong>{importedCodes.length}</strong> codes selected
                    {importedCodes.length > 0 && (
                      <button
                        type="button"
                        className="promo-clear-btn"
                        onClick={clearImportedCodes}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {importedCodes.length > 0 && (
                    <div className="promo-code-preview">
                      {importedCodes.slice(0, 12).map((code) => (
                        <span key={code}>{code}</span>
                      ))}

                      {importedCodes.length > 12 && (
                        <span>+{importedCodes.length - 12} more</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {form.rewardType === "GIFT_VOUCHER" && (
                <div className="promo-field">
                  <label>Minimum Qty</label>
                  <input
                    type="number"
                    value={form.minQty}
                    onChange={(e) => handleChange("minQty", e.target.value)}
                    placeholder="1"
                  />
                  <small>
                    2 ထားရင် item 2 ခု ပြည့်မှ gift voucher ထွက်မယ်။
                  </small>
                </div>
              )}

              {!(form.rewardType === "GIFT_ITEM" && form.giftRule === "ELIGIBLE_AMOUNT_TIERS") && (
                <div className="promo-field">
                  <label>Minimum Amount</label>
                  <input
                    type="number"
                    value={form.minAmount}
                    onChange={(e) => handleChange("minAmount", e.target.value)}
                    placeholder="0"
                  />
                  <small>
                    0 ထားရင် amount မစစ်ပါ။ 80000 ထားရင် 80,000 Ks ပြည့်မှ
                    promotion apply ဖြစ်မယ်။
                  </small>
                </div>
              )}

              {form.rewardType === "GIFT_VOUCHER" && (
                <>
                  <div className="promo-field">
                    <label>Gift Voucher Amount *</label>
                    <input
                      type="number"
                      value={form.giftAmount}
                      onChange={(e) =>
                        handleChange("giftAmount", e.target.value)
                      }
                      placeholder="10000"
                    />
                  </div>

                  <div className="promo-field">
                    <label>Gift Voucher Expire Date *</label>
                    <input
                      type="date"
                      value={form.giftExpireDate}
                      onChange={(e) =>
                        handleChange("giftExpireDate", e.target.value)
                      }
                    />
                  </div>
                </>
              )}

              {form.rewardType === "INSTANT_DISCOUNT" && (
                <>
                  <div className="promo-field">
                    <label>Discount Rule</label>
                    <select
                      value={form.discountRule}
                      onChange={(e) =>
                        handleChange("discountRule", e.target.value)
                      }
                    >
                      <option value="EVERY_N_ITEMS_DISCOUNT_M_ITEMS">
                        Every N Items → Discount M Cheapest Items
                      </option>
                    </select>
                    <small>
                      Fixed Bundle Total ကိုရွေးရင် Group Qty အရေအတွက်ကို
                      သတ်မှတ်ထားတဲ့ စုစုပေါင်းဈေးနဲ့တွက်မယ်။
                    </small>
                  </div>

                  <div className="promo-field">
                    <label>Group Qty</label>
                    <input
                      type="number"
                      value={form.groupQty}
                      onChange={(e) => handleChange("groupQty", e.target.value)}
                      placeholder="2"
                    />
                    <small>
                      2 ထားရင် item 2 ခုတိုင်း discount item တွက်မယ်။
                    </small>
                  </div>

                  {form.discountType !== "FIXED_BUNDLE_PRICE" && (
                    <div className="promo-field">
                      <label>Discount Item Qty</label>
                      <input
                        type="number"
                        value={form.discountItemQty}
                        onChange={(e) =>
                          handleChange("discountItemQty", e.target.value)
                        }
                        placeholder="1"
                      />
                      <small>
                        Group တစ်ခုတိုင်း cheapest item ဘယ်နှစ်ခုလျှော့မလဲ။
                      </small>
                    </div>
                  )}

                  <div className="promo-field">
                    <label>Discount Type</label>
                    <select
                      value={form.discountType}
                      onChange={(e) => handleDiscountTypeChange(e.target.value)}
                    >
                      <option value="FIXED_BUNDLE_PRICE">
                        Fixed Bundle Total
                      </option>
                      <option value="PERCENT">Percent Off</option>
                      <option value="AMOUNT">Amount Off</option>
                      <option value="FREE">Free</option>
                    </select>
                    <small>
                      Fixed Bundle Total = ရွေးထားတဲ့ item အရေအတွက်ရဲ့
                      နောက်ဆုံးစုစုပေါင်းဈေး။
                    </small>
                  </div>

                  {form.discountType === "FIXED_BUNDLE_PRICE" && (
                    <div className="promo-field promo-wide-field promo-bundle-groups">
                      <label>Bundle Groups (one promotion)</label>
                      <small>
                        Group တစ်ခုချင်းမှာ code တွေထည့်ပြီး bundle total price
                        သတ်မှတ်ပါ။ Size / color မစစ်ပါ။
                      </small>
                      {bundleGroups.map((group, index) => (
                        <div className="promo-bundle-group" key={group.groupId}>
                          <div className="promo-bundle-group-heading">
                            <strong>Bundle Group {index + 1}</strong>
                            <span>{getBundleGroupCodeList(group).length} codes</span>
                          </div>
                          <div className="promo-bundle-group-fields">
                            <input
                              value={group.groupId}
                              onChange={(e) =>
                                updateBundleGroup(index, "groupId", e.target.value)
                              }
                              placeholder="Bundle group name"
                            />
                            <input
                              type="number"
                              value={group.bundlePrice}
                              onChange={(e) =>
                                updateBundleGroup(
                                  index,
                                  "bundlePrice",
                                  e.target.value,
                                )
                              }
                              placeholder="90000"
                            />
                          </div>
                          <textarea
                            value={group.productCodesText}
                            onChange={(e) =>
                              updateBundleGroup(
                                index,
                                "productCodesText",
                                e.target.value,
                              )
                            }
                            placeholder={
                              index === 0
                                ? "10529-031-001-70\\n10539-031-001-70"
                                : "10529-131-001-70\\n10539-131-001-70"
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {form.discountType !== "FREE" &&
                    form.discountType !== "FIXED_BUNDLE_PRICE" && (
                    <div className="promo-field">
                      <label>
                        {form.discountType === "PERCENT"
                          ? "Discount Percent"
                          : form.discountType === "FIXED_BUNDLE_PRICE"
                            ? "Bundle Total Price"
                            : "Discount Amount"}
                      </label>
                      <input
                        type="number"
                        value={form.discountValue}
                        onChange={(e) =>
                          handleChange("discountValue", e.target.value)
                        }
                        placeholder={
                          form.discountType === "PERCENT"
                            ? "12"
                            : form.discountType === "FIXED_BUNDLE_PRICE"
                              ? "90000"
                              : "10000"
                        }
                      />
                      <small>
                        {form.discountType === "PERCENT"
                          ? "12 ဆို 12% off ဖြစ်မယ်။"
                          : form.discountType === "FIXED_BUNDLE_PRICE"
                            ? "90000 ဆို Group Qty ပြည့်တဲ့ items ကို စုစုပေါင်း 90,000 Ks နဲ့တွက်မယ်။"
                            : "10000 ဆို 10,000 Ks off ဖြစ်မယ်။"}
                      </small>
                    </div>
                  )}

                  {form.discountType === "FREE" && (
                    <div className="promo-field">
                      <label>Discount Value</label>
                      <input value="100% Free" disabled />
                      <small>
                        Free ဆို cheapest item ကို 100% off လုပ်မယ်။
                      </small>
                    </div>
                  )}

                  {form.discountType !== "FIXED_BUNDLE_PRICE" && (
                    <div className="promo-field">
                      <label>Discount Target</label>
                      <select
                        value={form.discountTarget}
                        onChange={(e) =>
                          handleChange("discountTarget", e.target.value)
                        }
                      >
                        <option value="CHEAPEST">Cheapest Items</option>
                      </select>
                    </div>
                  )}

                  <div className="promo-field">
                    <label>Example</label>
                    <div className="promo-import-box">
                      {form.discountType === "FIXED_BUNDLE_PRICE"
                        ? `Any ${form.groupQty} selected items = ${Number(
                            form.discountValue || 0,
                          ).toLocaleString()} Ks total`
                        : form.discountType === "FREE"
                          ? `Buy ${form.groupQty}, cheapest ${form.discountItemQty} free`
                          : form.discountType === "PERCENT"
                            ? `Buy ${form.groupQty}, cheapest ${form.discountItemQty} ${form.discountValue}% off`
                            : `Buy ${form.groupQty}, cheapest ${form.discountItemQty} ${Number(
                                form.discountValue || 0,
                              ).toLocaleString()} Ks off`}
                    </div>
                  </div>
                </>
              )}
              {renderGiftItemTimerFields()}

              <div className="promo-field">
                <label>Status</label>
                <select
                  value={form.active ? "active" : "inactive"}
                  onChange={(e) =>
                    handleChange("active", e.target.value === "active")
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <button
              type="button"
              className="promo-create-btn"
              onClick={handleCreatePromotion}
            >
              Create Promotion
            </button>

            {message && <div className="promo-message">{message}</div>}
          </div>
        ) : (
          <div className="promo-card">
            <div className="promo-list-header">
              <div>
                <h3>Promotion List</h3>
                <p>Active / inactive promotions and gift voucher settings.</p>
              </div>

              <input
                className="promo-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search promotion / shop / mode..."
              />
            </div>

            <div className="promo-table-wrap">
              <table className="promo-table">
                <thead>
                  <tr>
                    <th>Promo ID</th>
                    <th>Name</th>
                    <th>Date Range</th>
                    <th>Reward</th>
                    <th>Flow</th>
                    <th>Issue Group</th>
                    <th>Redeem Group</th>
                    <th>Item Mode</th>
                    <th>Selected Count</th>
                    <th>Min / Group</th>
                    <th>Min Amount</th>
                    <th>Gift / Discount</th>
                    <th>Expire / Rule</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredPromotions.length === 0 ? (
                    <tr>
                      <td colSpan="15" className="promo-empty">
                        No promotions found
                      </td>
                    </tr>
                  ) : (
                    filteredPromotions.map((p) => {
                      const id = p._key || p.promoId;
                      const isInstant = p.rewardType === "INSTANT_DISCOUNT";

                      const promoTimeStatus = getPromotionTimeStatus(
                        p,
                        currentTime,
                      );

                      return (
                        <tr key={id}>
                          <td className="promo-code">{id}</td>
                          <td>{p.name || "-"}</td>
                          <td>
                            <div>
                              {p.startDate || "-"} {p.startTime || "00:00"} →{" "}
                              {p.endDate || "-"} {p.endTime || "23:59"}
                            </div>

                            <div
                              className={`promo-time-badge ${promoTimeStatus.type.toLowerCase()}`}
                            >
                              <strong>{promoTimeStatus.label}</strong>
                              {promoTimeStatus.subText && (
                                <span>{promoTimeStatus.subText}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="promo-badge">
                              {isInstant ? "Instant Discount" : p.rewardType === "GIFT_ITEM" ? "Gift Item" : "Gift Voucher"}
                            </span>
                          </td>

                          <td>
                            {p.promotionTypeLabel || p.promotionType || "-"}
                          </td>

                          <td>
                            <span className="promo-badge">
                              {p.issueShopGroup || "-"}
                            </span>
                          </td>

                          <td>
                            <span className="promo-badge">
                              {isInstant ? "-" : p.redeemShopGroup || "-"}
                            </span>
                          </td>

                          <td>
                            <span className="promo-badge">
                              {p.itemMode || "ALL"}
                            </span>
                          </td>

                          <td>{Number(p.selectedItemCount || 0)}</td>

                          <td>
                            {p.giftRule === "ELIGIBLE_AMOUNT_TIERS"
                              ? "Amount tiers"
                              : isInstant
                              ? `Group ${Number(p.groupQty || 2)}`
                              : Number(p.minQty || 1)}
                          </td>

                          <td>
                            {Number(p.minAmount || 0).toLocaleString()} Ks
                          </td>

                          <td>
                            {p.giftRule === "ELIGIBLE_AMOUNT_TIERS"
                              ? (p.giftAmountTiers || []).map((tier) =>
                                  `${Number(tier.minAmount).toLocaleString()}–${tier.maxAmount == null ? "∞" : Number(tier.maxAmount).toLocaleString()} Ks`
                                ).join(" / ")
                              : isInstant
                              ? p.discountType === "FREE"
                                ? "Free"
                                : p.discountType === "FIXED_BUNDLE_PRICE" &&
                                    Array.isArray(p.bundleGroups) &&
                                    p.bundleGroups.length > 0
                                  ? p.bundleGroups
                                      .map((group) =>
                                        `${Number(
                                          group.bundlePrice || group.discountValue || 0,
                                        ).toLocaleString()} Ks`,
                                      )
                                      .join(" / ")
                                : p.discountType === "PERCENT"
                                  ? `${Number(p.discountValue || 0)}%`
                                  : `${Number(
                                      p.discountValue || 0,
                                    ).toLocaleString()} Ks`
                              : `${Number(
                                  p.giftAmount || 0,
                                ).toLocaleString()} Ks`}
                          </td>

                          <td>
                            {p.giftRule === "ELIGIBLE_AMOUNT_TIERS"
                              ? (p.giftAmountTiers || []).map((tier) =>
                                  (tier.gifts || []).map((gift) => `${gift.name} x${gift.qty}`).join(" + ")
                                ).join(" / ") + (p.excludeSelectedPromotionItems ? " · Selected Items excluded" : "")
                              : isInstant
                              ? getInstantRuleLabel(p)
                              : p.giftExpireDate || "-"}
                          </td>

                          <td>
                            {p.active ? (
                              <span className="promo-status active">
                                Active
                              </span>
                            ) : (
                              <span className="promo-status inactive">
                                Inactive
                              </span>
                            )}
                          </td>

                          <td>
                            <div className="promo-row-actions">
                              <button
                                type="button"
                                className="promo-history-btn"
                                onClick={() =>
                                  navigate(
                                    p.rewardType === "INSTANT_DISCOUNT"
                                      ? `/erp/promotion-discount-history?promoId=${encodeURIComponent(id)}`
                                      : `/erp/promotion-history?promoId=${encodeURIComponent(id)}`,
                                  )
                                }
                              >
                                History
                              </button>

                              <button
                                type="button"
                                className={
                                  p.active
                                    ? "promo-action-btn danger"
                                    : "promo-action-btn"
                                }
                                onClick={() => handleToggleActive(p)}
                              >
                                {p.active ? "Disable" : "Enable"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
